import produce from 'immer';

import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { Loaders } from '@types';

import { Drawers } from '../../drawers';
import { Modals } from '../../modals';
import { StoreHelper } from '../storeHelper';

export interface FeedbackState {
	visibleLoaders: Partial<Record<Loaders, number>>;
	visibleModals: Partial<Record<Modals, number>> & { action?: string };
	visibleDrawers: Partial<Record<Drawers, number>>;
	insuranceDrawers: {
		showLife: boolean;
		showPpp: boolean;
	};
	simulationDrawerContinueEnable: {
		simulatorContinueEnable: boolean;
		insurancesContinueEnable: boolean;
	};
	scrollsToShoot: {
		lastStepSaveSimulation: boolean;
	};
	cameFromFaqs: {
		fromFaqs: boolean;
	};
}

export const initialFeedbackState: FeedbackState = {
	visibleLoaders: {},
	visibleModals: {},
	visibleDrawers: {},
	insuranceDrawers: {
		showLife: false,
		showPpp: false,
	},
	simulationDrawerContinueEnable: {
		simulatorContinueEnable: false,
		insurancesContinueEnable: false,
	},
	scrollsToShoot: {
		lastStepSaveSimulation: false,
	},
	cameFromFaqs: {
		fromFaqs: false,
	},
};

interface UseFeedbackStoreOutput extends FeedbackState {
	showLoader: (loaderName: Loaders) => void;
	hideLoader: (loaderName: Loaders) => void;
	showModal: (modalName: Modals, action?: string) => void;
	hideModal: (modalName: Modals, action?: string) => void;
	showDrawer: (drawerName: Drawers) => void;
	hideDrawer: (drawerName: Drawers) => void;
	setInsuranceDrawers: (showLife: boolean, showPpp: boolean) => void;
	setSimulatorScreenContinueEnable: (enable?: boolean) => void;
	setInsurancesScreenContinueEnable: (enable?: boolean) => void;
	setLastStepSaveSimulation: (fromSaveSimulation?: boolean) => void;
	setCameFromFaqs: (fromFaqs: boolean) => void;
}

export const useFeedbackStore = StoreHelper.createStore<UseFeedbackStoreOutput>(
	(set) => ({
		...initialFeedbackState,
		showLoader: function (loaderName: Loaders) {
			set(
				produce((state: FeedbackState) =>
					handleFeedbackStateVisibilityChange(state, 'visibleLoaders', loaderName, true)
				),
				false,
				'showLoader'
			);
		},
		hideLoader: function (loaderName: Loaders) {
			set(
				produce((state: FeedbackState) =>
					handleFeedbackStateVisibilityChange(state, 'visibleLoaders', loaderName, false)
				),
				false,
				'hideLoader'
			);
		},
		showModal: function (modalName: Modals, action?: string) {
			set(
				produce((state: FeedbackState) =>
					handleFeedbackStateVisibilityChange(state, 'visibleModals', modalName, true, action)
				),
				false,
				'showModal'
			);
		},
		hideModal: function (modalName: Modals, action?: string) {
			set(
				produce((state: FeedbackState) =>
					handleFeedbackStateVisibilityChange(state, 'visibleModals', modalName, false, action)
				),
				false,
				'hideModal'
			);
		},
		showDrawer: function (drawerName: Drawers) {
			set(
				produce((state: FeedbackState) =>
					handleFeedbackStateVisibilityChange(state, 'visibleDrawers', drawerName, true)
				),
				false,
				'showDrawer'
			);
		},
		hideDrawer: function (drawerName: Drawers) {
			set(
				produce((state: FeedbackState) =>
					handleFeedbackStateVisibilityChange(state, 'visibleDrawers', drawerName, false)
				),
				false,
				'hideDrawer'
			);
		},
		setInsuranceDrawers: function (isLifeClicked: boolean, isPppClicked: boolean) {
			set(
				produce((state: FeedbackState) => ({
					...state,
					insuranceDrawers: {
						showLife: isLifeClicked,
						showPpp: isPppClicked,
					},
				})),
				false,
				'setInsuranceDrawers'
			);
		},
		setSimulatorScreenContinueEnable: function (enable?: boolean) {
			set(
				produce((state: FeedbackState) => ({
					...state,
					simulationDrawerContinueEnable: {
						...state.simulationDrawerContinueEnable,
						simulatorContinueEnable: CoreHelper.isNullOrEmpty(enable) ? true : enable,
					},
				})),
				false,
				'setSimulatorScreenContinueEnable'
			);
		},
		setInsurancesScreenContinueEnable: function (enable?: boolean) {
			set(
				produce((state: FeedbackState) => ({
					...state,
					simulationDrawerContinueEnable: {
						...state.simulationDrawerContinueEnable,
						insurancesContinueEnable: CoreHelper.isNullOrEmpty(enable) ? true : enable,
					},
				})),
				false,
				'setInsurancesScreenContinueEnable'
			);
		},
		setLastStepSaveSimulation: function (fromSaveSimulation?: boolean) {
			set(
				produce((state: FeedbackState) => ({
					...state,
					scrollsToShoot: {
						...state.scrollsToShoot,
						lastStepSaveSimulation: fromSaveSimulation,
					},
				})),
				false,
				'setLastStepSaveSimulation'
			);
		},
		setCameFromFaqs: function (fromFaqs: boolean) {
			set(
				produce((state: FeedbackState) => ({
					...state,
					cameFromFaqs: {
						...state.cameFromFaqs,
						fromFaqs,
					},
				})),
				false,
				'setCameFromFaqs'
			);
		},
	}),
	'@@CONSUMERLOANSPUBLIC/FEEDBACK/'
);

export const handleFeedbackStateVisibilityChange = (
	currentState: FeedbackState,
	key: keyof FeedbackState,
	name: Modals | Drawers | Loaders,
	show: boolean,
	action?: string
): Partial<FeedbackState> => {
	const currentValue = ((currentState[key] as Record<string, number>)[name] as number | undefined) || 0;

	let newValue = currentValue - (show ? -1 : 1);
	newValue = newValue < 0 ? 0 : newValue;

	return {
		...currentState,
		[key]: {
			...currentState[key],
			[name]: newValue,
			action,
		},
	};
};
